import React, { useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { LicensesActions } from "../_actions/LicensesActions";
import { LicenseTypesActions } from "../_actions/LicenseTypesActions";
import { BudgetsStore } from "../_reducers/budgetsRootReducer";

import { ContentSpace, MainPageWithButtons, CapsuleButton } from "../_components";
import { AssignLicenseView } from "./AssignLicenseView";
import { BulkAssignLicenseView } from "./BulkAssignLicenseView";
import { LicensesTable } from "./LicensesTable";
import { LICENSES_MODULE_VIEWS } from "./LICENSES_MODULE_VIEWS";
import { getSellableLicenseTypes } from "./getSellableLicenseTypes";
import { useIsReadOnlyEmployee } from "../_helpers/useIsReadOnlyEmployee";

export function LicensesModule() {

    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //GLOBAL STATE
    const licenses = useSelector(BudgetsStore.selectors.getLicenses);
    const licenseTypes = useSelector(BudgetsStore.selectors.getLicenseTypes);
    const secondaryView = useSelector(BudgetsStore.selectors.getLicensesModuleSecondaryView);
    const canAssign = getSellableLicenseTypes(licenseTypes).length > 0;
    const isReadOnly = useIsReadOnlyEmployee();

    //ACTION HANDLERS
    useEffect(() => {
        dispatch(LicensesActions.listLicenses());
        dispatch(LicenseTypesActions.listLicenseTypes());
    }, [dispatch]);

    const handleGoBack = useCallback(() => {
        dispatch(LicensesActions.changeSecondaryView(null));
    }, [dispatch]);

    //CLEAN UP
    useEffect(() => {
        return () => dispatch(LicensesActions.changeSecondaryView(null));
    }, [dispatch]);

    //VIEW
    return (
        <ContentSpace
            mainContent={
                <MainPageWithButtons
                    pageTitle={t("mod_budgets_licenses_title")}
                    content={content()}
                    buttons={createButtons()}
                    size={'X_LARGE'}
                />
            }
            detailedContent={getSecondaryView()}
            onGoBack={handleGoBack}
        />
    );


    function createButtons() {
        if (isReadOnly)
            return [];

        return [
            <CapsuleButton
                key="assign"
                onClick={handleAssignLicense}
                text={t("mod_budgets_assign")}
                isEnabled={canAssign}
            />,
            <CapsuleButton
                key="assign-bulk"
                onClick={handleAssignLicenseBulk}
                text={t("mod_budgets_assign_bulk")}
                isEnabled={canAssign}
            />
        ];
    }

    function handleLicenseChange() {
        dispatch(LicenseTypesActions.listLicenseTypes());
    }

    function content() {
        return (
            <LicensesTable licenses={licenses} licenseTypes={licenseTypes} onLicenseChange={handleLicenseChange} />
        );
    }

    function handleAssignSuccess() {
        dispatch(LicensesActions.listLicenses());
        dispatch(LicenseTypesActions.listLicenseTypes());
    }

    function getSecondaryView() {
        switch (secondaryView) {
            case LICENSES_MODULE_VIEWS.LICENSE_ASSIGN:
                return <AssignLicenseView licenseTypes={licenseTypes} onAssignSuccess={handleAssignSuccess}/>
            case LICENSES_MODULE_VIEWS.LICENSE_ASSIGN_BULK:
                return <BulkAssignLicenseView licenseTypes={licenseTypes} onAssignSuccess={handleAssignSuccess}/>
            default:
                return null;
        }
    }

    //ACTION HANDLERS
    function handleAssignLicense() {
        dispatch(LicensesActions.changeSecondaryView(LICENSES_MODULE_VIEWS.LICENSE_ASSIGN));
    }

    function handleAssignLicenseBulk() {
        dispatch(LicensesActions.changeSecondaryView(LICENSES_MODULE_VIEWS.LICENSE_ASSIGN_BULK));
    }
}
